// Visual-harness hooks for the app page (index.html), installed before the world loads so the
// harness can await `ready`; the view and services attach once they exist.
//
//   harness shot ──► applyShot: leader → date → lens → mode → panel → camera pose
//   isSettled: services loaded, camera at rest, no terrain chunk pending
//
// A shot is a complete description of the screen: what it leaves out goes back to the start state
// (default leader, as-of date, no lens, 3D, no panel, nothing selected), so a shot looks the same
// whichever shot ran before it and whichever subset is run. The one shot that shows the start
// screen is START_SCREEN_SHOT: with no leader chosen the map sits behind the leader picker.
//
// Director mode (`?director=1`, director.ts) swaps the wall-clock loop for one the recorder steps:
// attach() stops the engine and adds the director methods to `__HARNESS__`, and `setShot` settles
// by stepping instead of waiting for paints.

import type { PanelId, Services } from '../services/api.ts';
import type { CoreMapView } from '../view/index.ts';
import type { Shot } from './harness-contract.ts';
import type { Store } from '../services/state.ts';
import { installHarness, type HarnessBridge } from './harness-bridge.ts';
import { createDirector, type DirectorPage } from './director.ts';
import { createPage } from './director-page.ts';

/** The one signal the actions cannot write: back to null is the start screen. */
export type HarnessStore = Pick<Store, 'leader'>;

const START_MODE = '3d';
const START_SCREEN_SHOT = 'leader-select';
const DEFAULT_LEADER = 'trump';

export interface AppHarness {
  /** The view and services exist: shots can be applied. */
  attach(view: CoreMapView, services: Services, store?: HarnessStore): void;
  markReady(): void;
  fail(reason: unknown): void;
  /** Call once per rendered frame. */
  frame(): void;
}

export interface AppHarnessOptions {
  /** `director`: the recorder owns every clock (director.ts). Default `live`. */
  mode?: 'live' | 'director';
  /** The page side of director mode; the browser's (director-page.ts) unless a test supplies one. */
  makePage?: () => DirectorPage;
}

export function installAppHarness(options: AppHarnessOptions = {}): AppHarness {
  const { mode = 'live', makePage = createPage } = options;
  let view: CoreMapView | null = null;
  let services: Services | null = null;
  let store: HarnessStore | null = null;

  function applyShot(shot: Shot): void {
    if (!view || !services) {
      return;
    }
    const { actions, state } = services;

    // Start state first, so nothing of the previous shot shows through.
    actions.setPlaying(false);
    actions.select(null);
    if (!shot.leader && store) {
      store.leader.value = null;
    }

    // State next: choosing a leader flies to their capital, and the pose below then overrides it.
    const leader = shot.leader ?? (shot.name === START_SCREEN_SHOT ? null : DEFAULT_LEADER);
    if (leader) {
      actions.chooseLeader(leader);
    }
    actions.setDate(shot.date ?? state.asOf.peek());
    actions.setLens(shot.lens ?? null);
    actions.setMode(shot.mode ?? START_MODE);
    actions.openPanel((shot.panel ?? 'none') as PanelId);
    view.setPose({ lon: shot.lon, lat: shot.lat, height: shot.height, pitch: shot.pitch ?? null, yaw: shot.yaw ?? 0 });
  }

  const bridge: HarnessBridge = installHarness({
    applyShot,
    isSettled() {
      if (!view || !services || services.state.loading.peek()) {
        return false;
      }
      return view.core.camera.settled() && view.core.terrain.stats().pending === 0;
    },
    counters() {
      const s = view?.stats();
      return { drawCalls: s?.drawCalls ?? 0, triangles: s?.triangles ?? 0, backend: s?.backend ?? 'webgpu', unsourcedMarkers: view?.markers().unsourced ?? 0 };
    },
  });

  return {
    attach(v, s, st) {
      view = v;
      services = s;
      store = st ?? null;
      if (mode === 'director') {
        Object.assign(bridge.api, createDirector({ view: v, services: s, page: makePage(), apply: applyShot }));
      }
    },
    markReady: bridge.markReady,
    fail: bridge.fail,
    frame: bridge.frame,
  };
}
